import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import {
  Wrench,
  Package,
  Shield,
  Droplets,
  Activity,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
  Building2,
  ChevronRight,
  Bell,
  Settings,
  Search,
  Menu,
  X,
  FileText,
  Truck,
  Beaker,
  FolderKanban,
  Phone,
  Mail,
  Server,
  RefreshCw,
  ShoppingCart,
  ArrowRight,
  Play,
  Zap,
  Target,
  BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AskIsaac Logo - New [i] Brand Identity
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const ASKISAAC_LOGO_DARK = "/logos/askisaac-b-monogram.svg";

// Professional imagery
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/3a0e41db-bfc1-43c3-92e6-c3e7b85f9c4a/images/483d50a437b9e65f9135889159bed64bc1da56127bf07b1c2391e179099b97b7.png";
const STERILE_WORKER_IMAGE = "https://static.prod-images.emergentagent.com/jobs/3a0e41db-bfc1-43c3-92e6-c3e7b85f9c4a/images/df1c451991f3575bff240464575768af2f50f5f466dec6187244aee47107cb32.png";
const BIOMED_IMAGE = "https://static.prod-images.emergentagent.com/jobs/3a0e41db-bfc1-43c3-92e6-c3e7b85f9c4a/images/92d878c63cfad26c8111a6f9dfde0a46c21201547533d5309fca42bc72f18dbc.png";
const STERILE_MATE_LOGO = "https://sterilemate.com/wp-content/uploads/2022/01/sterile-mate-logo-white.svg";
// New imagery for specific services - diverse teams
const MEDICAL_SUPPLY_ROOM_IMAGE = "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/40749fd752dc283056ca0a3bb06a6427f6c0fa0bc2a8ee073894671604b794cd.png";
const LAB_WATER_TESTING_IMAGE = "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/80862ad50a64c462d25094c662d22a9ddacc8c9a34adf7cb88fa9905cc540f99.png";

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-3 py-2 rounded-lg shadow-lg text-sm border border-slate-200">
        <p className="font-medium text-slate-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-slate-600">
            {entry.name}: <span className="font-semibold" style={{ color: entry.color }}>{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Sidebar Component - Clean Professional Style
const Sidebar = ({ isOpen, onClose }) => {
  const navItems = [
    { icon: Activity, label: "Dashboard", path: "/", active: true },
    { icon: ShoppingCart, label: "Sourcing", path: "/hospital/backorders", highlight: true },
    { icon: Wrench, label: "Maintenance", path: "/maintenance" },
    { icon: Package, label: "Orders", path: "/order-request" },
    { icon: FolderKanban, label: "Projects", path: "/login" },
    { icon: Shield, label: "Compliance", path: "/st108" },
    { icon: Beaker, label: "Lab Testing", path: "/microbiology-testing" },
    { icon: Users, label: "Investors", path: "/investor" },
    { icon: Settings, label: "Admin", path: "/admin" }
  ];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white z-50 shadow-xl
        transform transition-transform duration-300 ease-in-out border-r border-slate-200
        lg:translate-x-0 ${isOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        {/* Logo */}
        <div className="p-5 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={ASKISAAC_LOGO} 
              alt="AskIsaac" 
              className="w-10 h-10 rounded-lg shadow-md"
            />
            <div>
              <h1 className="text-slate-900 font-bold text-lg tracking-tight font-manrope">AskIsaac</h1>
              <p className="text-slate-400 text-xs"></p>
            </div>
          </Link>
        </div>

        {/* ISAAC Acronym Explanation */}
        <div className="px-3 py-3 mx-3 mt-3 bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg border border-blue-100">
          <p className="text-[10px] text-slate-500 mb-2 font-medium uppercase tracking-wide">I.S.A.A.C. Framework</p>
          <div className="space-y-1 text-xs">
            <div className="flex items-start gap-2">
              <span className="text-blue-700 font-bold w-3">I</span>
              <span className="text-slate-600">nfrastructure</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-700 font-bold w-3">S</span>
              <span className="text-slate-600">ourcing</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-700 font-bold w-3">A</span>
              <span className="text-slate-600">sset</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-700 font-bold w-3">A</span>
              <span className="text-slate-600">dministration</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-700 font-bold w-3">C</span>
              <span className="text-slate-600">ompliance</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-0.5 mt-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative
                  ${item.active 
                    ? "bg-blue-700 text-white shadow-sm" 
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }
                `}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                <span className="font-medium text-sm">{item.label}</span>
                {item.highlight && !item.active && (
                  <span className="ml-auto px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">
                    NEW
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Contact */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-slate-50/50">
          <div className="space-y-2">
            <a href="tel:800-988-3892" className="flex items-center gap-2 text-slate-500 text-sm hover:text-blue-700 transition-colors">
              <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>800.988.3892</span>
            </a>
            <a href="mailto:info@sterilemate.com" className="flex items-center gap-2 text-slate-500 text-sm hover:text-blue-700 transition-colors">
              <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>info@sterilemate.com</span>
            </a>
          </div>
        </div>
      </aside>
    </>
  );
};

// Stat Card - Clean Professional Style
const StatCard = ({ icon: Icon, label, value, trend, trendUp, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    purple: "bg-violet-50 text-violet-700"
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 ${colorClasses[color]} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trendUp ? "text-emerald-600" : "text-red-500"}`}>
            <TrendingUp className={`w-3 h-3 ${!trendUp && "rotate-180"}`} strokeWidth={2} />
            <span>{trend}</span>
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold text-slate-900 font-manrope">{value}</p>
        <p className="text-sm text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
};

// Service Card - Professional Style with Image
const ServiceCard = ({ icon: Icon, title, description, path, image, testId }) => {
  return (
    <Link
      to={path}
      data-testid={testId}
      className="group relative overflow-hidden bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-all duration-300"
    >
      {/* Image Section */}
      <div className="relative h-32 overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        <div className="absolute bottom-3 left-3">
          <div className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <Icon className="w-4 h-4 text-blue-700" strokeWidth={1.5} />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="text-base font-semibold text-slate-900 font-manrope">{title}</h3>
        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{description}</p>
        <div className="flex items-center gap-1 text-blue-700 font-medium text-sm mt-3 group-hover:gap-2 transition-all">
          <span>Learn More</span>
          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </div>
    </Link>
  );
};

// Quick Action Button
const QuickAction = ({ icon: Icon, label, path }) => (
  <Link 
    to={path}
    className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-all group"
  >
    <div className="w-9 h-9 bg-slate-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors">
      <Icon className="w-4 h-4 text-slate-600 group-hover:text-blue-700" strokeWidth={1.5} />
    </div>
    <span className="text-sm font-medium text-slate-700 group-hover:text-blue-700">{label}</span>
    <ChevronRight className="w-4 h-4 text-slate-400 ml-auto group-hover:text-blue-600" strokeWidth={1.5} />
  </Link>
);

export default function AskIsaacDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    activeRequests: 0,
    completedToday: 0,
    pendingOrders: 0,
    equipmentOnline: 0
  });
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, weeklyRes, monthlyRes, equipmentRes] = await Promise.all([
          fetch(`${API_URL}/api/public/dashboard/stats`).catch(() => null),
          fetch(`${API_URL}/api/public/dashboard/weekly-activity`).catch(() => null),
          fetch(`${API_URL}/api/public/dashboard/monthly-trends`).catch(() => null),
          fetch(`${API_URL}/api/public/dashboard/equipment-status`).catch(() => null)
        ]);

        if (statsRes?.ok) setStats(await statsRes.json());
        if (weeklyRes?.ok) setWeeklyData((await weeklyRes.json()).data || []);
        if (monthlyRes?.ok) setMonthlyData((await monthlyRes.json()).data || []);
        if (equipmentRes?.ok) setEquipmentData((await equipmentRes.json()).data || []);
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fallback data
  const displayWeeklyData = weeklyData.length > 0 ? weeklyData : [
    { day: "Mon", requests: 12, completed: 10 },
    { day: "Tue", requests: 19, completed: 15 },
    { day: "Wed", requests: 15, completed: 14 },
    { day: "Thu", requests: 22, completed: 18 },
    { day: "Fri", requests: 18, completed: 16 },
    { day: "Sat", requests: 8, completed: 8 },
    { day: "Sun", requests: 5, completed: 5 }
  ];

  const displayEquipmentData = equipmentData.length > 0 ? equipmentData : [
    { name: "Operational", value: 85, color: "#10b981" },
    { name: "Maintenance", value: 10, color: "#f59e0b" },
    { name: "Critical", value: 5, color: "#ef4444" }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-4 md:px-6 py-3">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(true)} 
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <div className="hidden md:flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <input 
                  type="text" 
                  placeholder="Search requests, orders, equipment..." 
                  className="bg-transparent text-sm text-slate-600 outline-none w-64 placeholder:text-slate-400"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
                <Bell className="w-5 h-5" strokeWidth={1.5} />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
              </button>
              <Link 
                to="/login" 
                className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors"
                data-testid="header-login-btn"
              >
                <Users className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Staff Portal</span>
              </Link>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-2xl mb-8">
            {/* Background Image */}
            <div className="absolute inset-0">
              <img 
                src={HERO_IMAGE}
                alt="Hospital Water Purification Systems"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-900/85 to-blue-900/70" />
            </div>
            
            {/* Content */}
            <div className="relative z-10 p-6 md:p-10 lg:p-12">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <img src={STERILE_MATE_LOGO} alt="Sterile Mate" className="h-6 opacity-90" />
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-manrope tracking-tight mb-4">
                  Welcome to AskIsaac
                </h1>
                <p className="text-blue-100 text-lg md:text-xl mb-6 leading-relaxed">
                  Your healthcare concierge—helping you manage your everyday supply sourcing, water purification tasks, sterile processing compliance, service repairs, and regulatory compliance.
                </p>
                
                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Link 
                    to="/hospital/backorders"
                    className="inline-flex items-center gap-2 bg-white text-blue-900 px-5 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
                    Start Sourcing
                  </Link>
                  <Link 
                    to="/st108"
                    className="inline-flex items-center gap-2 bg-blue-800/50 text-white px-5 py-3 rounded-lg font-medium hover:bg-blue-800/70 transition-colors border border-blue-400/30"
                  >
                    <Shield className="w-5 h-5" strokeWidth={1.5} />
                    View Compliance
                  </Link>
                </div>
              </div>
              
              {/* Stats Overlay */}
              <div className="absolute right-6 bottom-6 hidden lg:flex items-center gap-6 bg-white/10 backdrop-blur-md rounded-xl px-6 py-4 border border-white/20">
                <div className="text-center">
                  <p className="text-3xl font-bold text-white font-manrope">{stats.activeRequests || 24}</p>
                  <p className="text-blue-200 text-sm">Active Requests</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-white font-manrope">{stats.equipmentOnline || 142}</p>
                  <p className="text-blue-200 text-sm">Systems Online</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard 
              icon={Activity} 
              label="Active Requests" 
              value={stats.activeRequests || 24} 
              trend="+12%" 
              trendUp 
              color="blue" 
            />
            <StatCard 
              icon={CheckCircle2} 
              label="Completed Today" 
              value={stats.completedToday || 18} 
              trend="+8%" 
              trendUp 
              color="green" 
            />
            <StatCard 
              icon={Package} 
              label="Pending Orders" 
              value={stats.pendingOrders || 7} 
              color="amber" 
            />
            <StatCard 
              icon={Server} 
              label="Equipment Online" 
              value={stats.equipmentOnline || 142} 
              trend="+2" 
              trendUp 
              color="purple" 
            />
          </div>

          {/* How It Works Banner */}
          <Link to="/how-it-works" className="block mb-8">
            <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-600 rounded-2xl p-6 text-white hover:shadow-xl transition-all hover:scale-[1.01] cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Play className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1 font-manrope">See How We Fulfill Your Orders</h3>
                    <p className="text-blue-100 text-sm">Interactive guide: From request to delivery in 6 simple steps</p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold">50+</p>
                    <p className="text-blue-200 text-xs">Suppliers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">24-48hr</p>
                    <p className="text-blue-200 text-xs">Response</p>
                  </div>
                  <ChevronRight className="w-8 h-8 text-white/70" />
                </div>
              </div>
            </div>
          </Link>

          {/* Services Grid */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-900 font-manrope">ISAAC Services</h2>
              <span className="text-sm text-slate-500">Infrastructure · Sourcing · Assets · Compliance</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ServiceCard
                icon={ShoppingCart}
                title="Sourcing & Procurement"
                description="Request competitive quotes from our expanded supplier network."
                path="/hospital/backorders"
                image={MEDICAL_SUPPLY_ROOM_IMAGE}
                testId="service-sourcing"
              />
              <ServiceCard
                icon={Wrench}
                title="Maintenance & Repair"
                description="Submit service requests for equipment maintenance and repairs."
                path="/maintenance"
                image={BIOMED_IMAGE}
                testId="service-maintenance"
              />
              <ServiceCard
                icon={Shield}
                title="AAMI ST108 Compliance"
                description="Access water quality standards and compliance documentation."
                path="/st108"
                image={STERILE_WORKER_IMAGE}
                testId="service-compliance"
              />
              <ServiceCard
                icon={Beaker}
                title="Lab Testing Services"
                description="Order microbiology and water quality testing services."
                path="/microbiology-testing"
                image={LAB_WATER_TESTING_IMAGE}
                testId="service-lab"
              />
            </div>
          </div>

          {/* About Ask Isaac Section */}
          <div className="bg-gradient-to-br from-blue-900 to-slate-900 rounded-2xl p-6 md:p-8 mb-8 text-white">
            <h2 className="text-2xl font-bold font-manrope mb-4">About Ask Isaac</h2>
            <div className="space-y-4 text-blue-100 text-sm md:text-base leading-relaxed">
              <p>
                Ask Isaac is the intelligent infrastructure concierge powered by <strong className="text-white">Sterile Mate LLC</strong>, your dedicated healthcare partner. Built to be more than just a software portal, Isaac serves as a sophisticated, 24/7 digital extension of the Sterile Mate team, designed to bridge the gap between complex hospital engineering and seamless supply chain operations.
              </p>
              <p>
                The name <strong className="text-white">I.S.A.A.C.</strong> represents our five core pillars of service: <strong className="text-white">Infrastructure, Sourcing, Asset, Administration, and Compliance</strong>. Whether Isaac is hunting down critical backordered supplies through our global network, dispatching elite licensed contractors for emergency repairs, or proactively monitoring your facility's equipment warranties and AAMI ST108 compliance, he is engineered to protect your facility's uptime.
              </p>
              <p>
                By combining Sterile Mate's deep industry expertise with cutting-edge AI automation, Isaac ensures that your water purification systems, medical utilities, and project milestones are managed with the wisdom of a veteran engineer and the precision of a high-tech assistant. With Isaac on your side, Sterile Mate LLC provides a "well-spring" of reliability, ensuring your facility remains compliant, stocked, and ready for surgery.
              </p>
            </div>

            {/* I.S.A.A.C. Framework Table */}
            <div className="mt-6 pt-6 border-t border-blue-800/50">
              <h3 className="text-lg font-semibold font-manrope mb-4">The I.S.A.A.C. Framework</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-blue-800/50">
                      <th className="pb-3 pr-4 text-blue-300 font-medium w-16">Letter</th>
                      <th className="pb-3 pr-4 text-blue-300 font-medium w-32">Pillar</th>
                      <th className="pb-3 text-blue-300 font-medium">Isaac's Responsibility</th>
                    </tr>
                  </thead>
                  <tbody className="text-blue-100">
                    <tr className="border-b border-blue-800/30">
                      <td className="py-3 pr-4 font-bold text-white text-lg">I</td>
                      <td className="py-3 pr-4 font-semibold text-white">Infrastructure</td>
                      <td className="py-3">Managing the build-out and milestones of your physical water rooms.</td>
                    </tr>
                    <tr className="border-b border-blue-800/30">
                      <td className="py-3 pr-4 font-bold text-white text-lg">S</td>
                      <td className="py-3 pr-4 font-semibold text-white">Sourcing</td>
                      <td className="py-3">Using a blind-bid engine to secure backordered items at the best price.</td>
                    </tr>
                    <tr className="border-b border-blue-800/30">
                      <td className="py-3 pr-4 font-bold text-white text-lg">A</td>
                      <td className="py-3 pr-4 font-semibold text-white">Asset</td>
                      <td className="py-3">Tracking every pump, filter, and RO system by its unique Serial Number.</td>
                    </tr>
                    <tr className="border-b border-blue-800/30">
                      <td className="py-3 pr-4 font-bold text-white text-lg">A</td>
                      <td className="py-3 pr-4 font-semibold text-white">Administration</td>
                      <td className="py-3">Dispatching pre-vetted, licensed contractors and managing billing.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-bold text-white text-lg">C</td>
                      <td className="py-3 pr-4 font-semibold text-white">Compliance</td>
                      <td className="py-3">Monitoring COIs, trade licenses, and protecting you with warranty alerts.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Quick Actions + Charts Row */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Quick Actions */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-base font-semibold text-slate-900 font-manrope mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <QuickAction icon={ShoppingCart} label="New Backorder Request" path="/hospital/backorders" />
                <QuickAction icon={Wrench} label="Submit Maintenance Request" path="/maintenance" />
                <QuickAction icon={Package} label="Request Quote" path="/order-request" />
                <QuickAction icon={FileText} label="View Compliance Docs" path="/st108" />
              </div>
            </div>

            {/* Weekly Activity Chart */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-900 font-manrope">Weekly Activity</h3>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 bg-blue-500 rounded" />
                    <span className="text-slate-500">Requests</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 bg-emerald-500 rounded" />
                    <span className="text-slate-500">Completed</span>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={displayWeeklyData}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} width={30} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="requests" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Requests" />
                  <Bar dataKey="completed" fill="#10b981" radius={[3, 3, 0, 0]} name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Equipment Status */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-base font-semibold text-slate-900 font-manrope mb-4">Equipment Health</h3>
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie
                      data={displayEquipmentData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      dataKey="value"
                      stroke="none"
                    >
                      {displayEquipmentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-2">
                {displayEquipmentData.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-slate-600">{item.name}: {item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Compliance Badges */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900 font-manrope">Production Compliance</h3>
              <Link to="/st108" className="text-sm text-blue-700 font-medium hover:underline">View Details</Link>
            </div>
            <p className="text-sm text-slate-500 mb-5">
              Sterile Mate delivers products meeting the highest internationally recognized regulations.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              {["ISO", "ASTM", "EN", "CE", "FDA", "SGS"].map((badge) => (
                <div key={badge} className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg">
                  <span className="text-sm font-semibold text-slate-700">{badge}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <footer className="border-t border-slate-200 pt-8 pb-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-700 rounded-lg flex items-center justify-center">
                  <Droplets className="w-4 h-4 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">AskIsaac</p>
                  <p className="text-xs text-slate-500"></p>
                </div>
              </div>
              <div className="flex items-center gap-6 text-slate-500 text-sm">
                <a href="tel:800-988-3892" className="flex items-center gap-2 hover:text-blue-700 transition-colors">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  800.988.3892
                </a>
                <a href="mailto:info@sterilemate.com" className="flex items-center gap-2 hover:text-blue-700 transition-colors">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  info@sterilemate.com
                </a>
              </div>
            </div>
          </footer>
        </main>
      </div>
      
      {/* Global Footer with Sitemap */}
      <Footer />
    </div>
  );
}
